/**
 * หน้าจอ BOM และ Routing — สูตรที่ใช้อยู่ ฉบับก่อนหน้า และการเขียนฉบับถัดไป
 *
 * สิ่งที่หน้าจอนี้ต้องทำให้ผู้ใช้แยกออกจากกันให้ได้ และเป็นเหตุผลที่มันมีอยู่:
 *
 * 1. **ฉบับไหนคือฉบับที่ใช้อยู่ และใช้ตั้งแต่เมื่อไร** — คนที่อ่านผิดฉบับจะผลิตผิดสูตร
 * 2. **ฉบับที่ถูกแทนที่ไปแล้วยังอ่านได้** พร้อมช่วงเวลาที่เคยมีผล เพราะใบสั่งผลิตที่
 *    สั่งปล่อยไปแล้วผูกกับฉบับที่มันถูกสร้างมาจาก (`BUSINESS_RULES.md` ข้อ 2)
 * 3. **โรงงานไหน** — BOM/Routing เป็น plant-specific เสมอ และ `plant_code` เป็น
 *    ขอบเขตสิทธิ์ (D-004) ไม่ใช่แค่ตัวกรอง หน้าจอจึงเสนอเฉพาะโรงงานที่สิทธิ์ของผู้ใช้
 *    พามาถึงจริง และไม่เดาโรงงานแทนผู้ใช้เมื่อ URL ชี้ไปที่โรงงานนอกมุมมอง
 *
 * **ไม่มีปุ่มแก้ไขและไม่มีปุ่มลบ** ทั้งหน้าจอ เพราะกลุ่ม endpoint นี้ไม่มี `PATCH`
 * และไม่มี `DELETE` เลย (contract) — และหน้าจอพูดเรื่องนี้ตรงจุดที่ผู้ใช้จะมองหาปุ่ม
 * แก้ไข ไม่ใช่ปล่อยให้เดาว่าปุ่มหายไปไหน
 *
 * ⚠️ การเสนอ/ไม่เสนอฟอร์มที่นี่เป็น UX ไม่ใช่ด่านความปลอดภัย — flag อ่านจาก
 * `modules` ที่เซิร์ฟเวอร์ส่งมาผ่าน `can()` เท่านั้น ห้าม derive กติกา ADMIN/AUDITOR
 * ซ้ำจาก `system_roles` และเซิร์ฟเวอร์เป็นผู้ตัดสินทุกคำขอเสมอ
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { BomFailureNotice } from './BomFailureNotice';
import { FormulaPanel } from './FormulaPanel';
import { formulaSearch, resolveFormulaView, type FormulaKind } from './bomView';
import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { detailSearch, productStatusLabel, productStatusTone } from '../products/productView';
import { PRODUCT_MASTER_PATH } from '../routes';
import { fetchProduct } from '../../lib/products/client';
import type { Product } from '../../lib/products/types';
import { BOM_ROUTING_MODULE, BOM_SPEC, ROUTING_SPEC } from '../../lib/bom/types';
import type { FormulaFailure } from '../../lib/bom/client';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';

export interface BomRoutingScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
}

type ProductState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly product: Product }
  | FormulaFailure;

const TAB_LABELS: Readonly<Record<FormulaKind, string>> = {
  bom: 'สูตรการผลิต (BOM)',
  routing: 'ขั้นตอนการผลิต (Routing)',
};

export function BomRoutingScreen({
  permissions,
  apiBaseUrl,
  fetchImpl,
}: BomRoutingScreenProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = resolveFormulaView(searchParams);
  const { productId, kind } = view;

  const [state, setState] = useState<ProductState>({ status: 'loading' });

  useEffect(() => {
    if (productId === '') {
      return;
    }
    const controller = new AbortController();
    let active = true;

    setState({ status: 'loading' });

    void fetchProduct(productId, {
      apiBaseUrl,
      fetchImpl,
      signal: controller.signal,
    }).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      // ความล้มเหลวของทั้งสอง client มีรูปร่างเดียวกันทุกสถานะตาม contract
      // (`ProductFailure` กับ `FormulaFailure` สะท้อนตารางเดียวกันใน `docs/API_CONTRACT.md`)
      setState(result);
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [productId, apiBaseUrl, fetchImpl]);

  const product = state.status === 'ready' ? state.product : null;
  const canCreate = can(permissions, BOM_ROUTING_MODULE, 'CREATE');

  /**
   * โรงงานที่เลือกได้จริง — มาจาก `plant_data` ที่เซิร์ฟเวอร์ส่งมากับสินค้าตัวนี้
   * ซึ่งถูกกรองด้วย plant scope ของผู้เรียกแล้ว (D-004) ไม่ใช่รายการที่หน้าจอคิดเอง
   */
  const plants = product?.plant_data ?? [];
  const requestedPlant = view.plantCode;
  const selectedPlant =
    requestedPlant === ''
      ? (plants[0]?.plant_code ?? null)
      : (plants.find((entry) => entry.plant_code === requestedPlant)?.plant_code ?? null);

  const go = (next: { readonly plantCode?: string; readonly kind?: FormulaKind }) => {
    setSearchParams(
      formulaSearch({
        productId,
        plantCode: next.plantCode ?? selectedPlant ?? requestedPlant,
        kind: next.kind ?? kind,
      }),
      { replace: true },
    );
  };

  return (
    <div className="cap-page" data-testid="bom-routing-screen">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title" data-testid="bom-routing-product-code">
            {product === null ? 'BOM และ Routing' : product.product_code}
            <InfoHint
              label="เกี่ยวกับหน้า BOM และ Routing"
              text={
                product === null
                  ? 'สูตรการผลิตและขั้นตอนการผลิตของสินค้าหนึ่งตัวในโรงงานหนึ่งแห่ง'
                  : 'สูตรและขั้นตอนการผลิตตามฉบับที่มีผล'
              }
            />
          </h1>
          {product === null ? null : <p className="cap-page__sub">{product.name}</p>}
        </div>
        <div className="cap-page__actions">
          {product === null ? null : (
            <StatusBadge tone={productStatusTone(product.status)}>
              {productStatusLabel(product.status)}
            </StatusBadge>
          )}
          {productId === '' ? null : (
            <Link
              className="cap-button cap-button--ghost"
              data-testid="bom-routing-product-link"
              to={{ pathname: PRODUCT_MASTER_PATH, search: detailSearch(productId) }}
            >
              กลับไปหน้ารายละเอียดสินค้า
            </Link>
          )}
        </div>
      </div>

      {productId === '' ? (
        <div className="cap-notice" data-testid="bom-routing-no-product" role="status">
          <StatusBadge tone="info">ยังไม่ได้เลือกสินค้า</StatusBadge>
          <h2 className="cap-notice__title">เลือกสินค้าก่อนจึงจะดูสูตรได้</h2>
          <p className="cap-notice__text">
            BOM และ Routing เป็นของสินค้าหนึ่งตัวในโรงงานหนึ่งแห่งเสมอ —
            เปิดหน้าจอนี้จากปุ่ม “BOM และ Routing” ในหน้ารายละเอียดสินค้า
          </p>
          <div className="cap-notice__actions">
            <Link className="cap-button cap-button--primary" to={PRODUCT_MASTER_PATH}>
              ไปที่ข้อมูลสินค้า
            </Link>
          </div>
        </div>
      ) : null}

      {productId !== '' && state.status === 'loading' ? (
        <div className="cap-notice" data-testid="bom-routing-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดข้อมูลสินค้า…</p>
        </div>
      ) : null}

      {productId !== '' && state.status !== 'loading' && state.status !== 'ready' ? (
        <BomFailureNotice failure={state} context="product" plantCode={requestedPlant} />
      ) : null}

      {product === null ? null : (
        <>
          <section className="cap-filters" aria-label="โรงงานและชนิดของสูตร">
            <label className="cap-field">
              <span className="cap-field__label">
                โรงงาน (plant_code)
                <InfoHint
                  label="เกี่ยวกับการเลือกโรงงาน"
                  text="เลือกได้เฉพาะโรงงานที่คุณมีสิทธิ์เห็น (D-004) — สินค้าตัวเดียวกันมีสูตรคนละฉบับในอีกโรงงานได้ การเลือกผิดโรงงานคือการอ่าน (หรือเขียน) สูตรของโรงงานอื่น"
                />
              </span>
              <select
                className="cap-input"
                name="plant_code"
                data-testid="formula-plant-select"
                value={selectedPlant ?? ''}
                onChange={(event) => {
                  go({ plantCode: event.target.value });
                }}
              >
                {selectedPlant === null ? <option value="">— เลือกโรงงาน —</option> : null}
                {plants.map((entry) => (
                  <option key={entry.plant_id} value={entry.plant_code}>
                    โรงงาน {entry.plant_code}
                  </option>
                ))}
              </select>
            </label>

            <div className="cap-field" role="group" aria-label="ชนิดของสูตร">
              <span className="cap-field__label">ชนิดของสูตร</span>
              <div className="cap-notice__actions">
                {(['bom', 'routing'] as const).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    className={`cap-button ${kind === tab ? 'cap-button--primary' : 'cap-button--ghost'}`}
                    data-testid={`formula-tab-${tab}`}
                    aria-pressed={kind === tab}
                    onClick={() => {
                      go({ kind: tab });
                    }}
                  >
                    {TAB_LABELS[tab]}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {plants.length === 0 ? (
            <div className="cap-notice" data-testid="formula-no-plant" role="status">
              <StatusBadge tone="warning">ไม่มีโรงงานที่คุณเห็น</StatusBadge>
              <h2 className="cap-notice__title">
                ไม่มีโรงงานที่คุณมีสิทธิ์เห็นสำหรับสินค้านี้
              </h2>
              <p className="cap-notice__text">
                `plant_code` บังคับเสมอสำหรับ BOM และ Routing (D-004) เมื่อยังไม่มีโรงงานให้เลือก
                หน้าจอนี้จึงไม่ยิงคำขอที่รู้อยู่แล้วว่าไม่สมบูรณ์
              </p>
            </div>
          ) : null}

          {plants.length > 0 && selectedPlant === null ? (
            <div className="cap-notice" data-testid="formula-plant-out-of-scope" role="status">
              <StatusBadge tone="warning">โรงงานนอกมุมมองของคุณ</StatusBadge>
              <h2 className="cap-notice__title">
                ลิงก์นี้ชี้ไปที่โรงงาน {requestedPlant} ซึ่งไม่อยู่ในรายการที่คุณเห็น
              </h2>
              <p className="cap-notice__text">
                หน้าจอจะไม่สลับไปโรงงานอื่นให้เองเงียบ ๆ เพราะสูตรของอีกโรงงานหนึ่งไม่ใช่คำตอบของ
                คำถามที่คุณเปิดลิงก์นี้มาถาม — เลือกโรงงานจากรายการด้านบน หรือขอสิทธิ์โรงงานนี้
                กับผู้ดูแลระบบ
              </p>
            </div>
          ) : null}

          {selectedPlant === null ? null : kind === 'bom' ? (
            <FormulaPanel
              spec={BOM_SPEC}
              productId={productId}
              plantCode={selectedPlant}
              canCreate={canCreate}
              apiBaseUrl={apiBaseUrl}
              fetchImpl={fetchImpl}
            />
          ) : (
            <FormulaPanel
              spec={ROUTING_SPEC}
              productId={productId}
              plantCode={selectedPlant}
              canCreate={canCreate}
              apiBaseUrl={apiBaseUrl}
              fetchImpl={fetchImpl}
            />
          )}
        </>
      )}
    </div>
  );
}
